import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { api } from './useAuth';

const usePermissions = () => {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const fetchUserPermissions = async () => {
      try {
        if (!user) {
          setPermissions({});
          setLoading(false);
          return;
        }

        // Fetch permission groups to check user's permissions
        setLoading(true);
        const { data: permissionGroups } = await api.get('/permission-groups');
// isteğe bağlı sağlamlık:
if (!Array.isArray(permissionGroups)) throw new Error('Invalid response for permission groups');
        
        // Admin users get full permissions regardless of permission group
        // Check both permission group name and legacy admin role
        const isAdminUser = user.role === 'admin' || 
          (user.permissionGroupId && permissionGroups.find(group => group._id === user.permissionGroupId)?.name === 'Admin');
        
        if (isAdminUser) {
          console.log('✅ Admin user detected (role or permission group), setting admin permissions');
          const adminPermissions = {
            invoices: { view: true, create: true, update: true, delete: true },
            incomingInvoices: { view: true, create: true, update: true, delete: true },
            companies: { view: true, create: true, update: true, delete: true },
            customers: { view: true, create: true, update: true, delete: true },
            employees: { view: true, create: true, update: true, delete: true },
            users: { view: true, create: true, update: true, delete: true },
            settings: { view: true, create: true, update: true, delete: true },
            templates: { view: true, create: true, update: true, delete: true },
            standardDocuments: { view: true, create: true, update: true, delete: true },
            taxCalculation: { view: true, create: true, update: true, delete: true },
            smtpSettings: { view: true, create: true, update: true, delete: true },
            mailTemplates: { view: true, create: true, update: true, delete: true },
            permissionGroups: { view: true, create: true, update: true, delete: true },
            apiDocumentation: { view: true, create: false, update: false, delete: false }
          };
          setPermissions(adminPermissions);
          // Save permissions to localStorage for debugging
          localStorage.setItem('permissions', JSON.stringify(adminPermissions));
          setLoading(false);
          return;
        }

        // If user has a permission group, get their specific permissions
        // Otherwise, use the default permission group
        let userGroup;
        if (user.permissionGroupId) {
          userGroup = permissionGroups.find(group => group._id === user.permissionGroupId);
        }
        
        if (!userGroup) {
          // Get default group
          userGroup = permissionGroups.find(group => group.isDefault && group.isActive);
        }

        if (userGroup) {
          console.log('✅ User group found, setting permissions:', userGroup.name);
          // Convert permission array format to easier lookup object
          const rights = {};
          userGroup.permissions.forEach(permission => {
            rights[permission.module] = {
              view: permission.view,
              create: permission.create,
              update: permission.update,
              delete: permission.delete
            };
          });
          console.log('🔑 Final permissions:', rights);
          setPermissions(rights);
          // Save permissions to localStorage for debugging
          localStorage.setItem('permissions', JSON.stringify(rights));
        } else {
          // No group found, suggest no permissions
          setPermissions({});
          localStorage.setItem('permissions', JSON.stringify({}));
        }
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
        setPermissions({});
        localStorage.setItem('permissions', JSON.stringify({}));
      } finally {
        setLoading(false);
      }
    };

    fetchUserPermissions();
  }, [user]);

  // Helper functions to check permissions
  const hasPermission = (module, action) => {
    return permissions[module]?.[action] === true;
  };

  const canView = (module) => hasPermission(module, 'view');
  const canCreate = (module) => hasPermission(module, 'create');
  const canUpdate = (module) => hasPermission(module, 'update');
  const canDelete = (module) => hasPermission(module, 'delete');

  return {
    permissions,
    loading,
    hasPermission,
    canView,
    canCreate,
    canUpdate,
    canDelete
  };
};

export default usePermissions;
